/**
 * DataIntelligenceBar — Mobile-friendly floating toolbar for Data Intelligence.
 * Always visible at the top of the screen. Controls data source, depth field,
 * category filter column. Bucket buttons are in BucketPanel at the bottom.
 */
import React, { useState } from 'react';

import type { DataSourceKind } from '@arkturian/wave-engine/data/DataIntelligenceController';
type AgentFace = 'chat' | 'card';

interface DataIntelligenceBarProps {
    dataSource: DataSourceKind;
    onSourceChange: (source: DataSourceKind) => void;
    loading: boolean;
    onealCount: number;
    // Depth
    depthFields: { label: string; field: string }[];
    activeDepthField: string;
    onDepthFieldChange: (field: string) => void;
    onSort: () => void;
    // Category filter columns
    categoricColumns: string[];
    activeColumn: string;
    onColumnChange: (col: string) => void;
    // GPANE auto-recommend
    onAutoRecommend?: () => void;
    recommendedColumn?: string;
    // AgentOS (2026-09-16): one tile per agent, face = chat context or generic card
    agentCount?: number;
    agentFace?: AgentFace;
    agentStatus?: string;
    agentLoading?: boolean;
    onAgentFaceChange?: (face: AgentFace) => void;
}

const pill = (active: boolean, activeColor: string, inactiveColor: string): React.CSSProperties => ({
    flex: '0 0 auto',
    padding: '5px 10px',
    fontSize: '11px',
    fontWeight: active ? 700 : 500,
    color: active ? '#fff' : inactiveColor,
    background: active ? activeColor : 'rgba(255,255,255,0.08)',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
});

const DataIntelligenceBar: React.FC<DataIntelligenceBarProps> = ({
    dataSource, onSourceChange, loading, onealCount,
    depthFields, activeDepthField, onDepthFieldChange, onSort,
    categoricColumns, activeColumn, onColumnChange,
    onAutoRecommend, recommendedColumn,
    agentCount = 0, agentFace = 'chat', agentStatus = '', agentLoading = false, onAgentFaceChange,
}) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0,
            zIndex: 9999,
            background: 'rgba(8,12,24,0.92)',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid rgba(59,130,246,0.15)',
            padding: expanded ? '8px 10px 10px' : '8px 10px',
            transition: 'all 0.2s ease',
        }}>
            {/* Row 1: Data Source + expand toggle */}
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <span style={{ color: '#64748b', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: '4px' }}>DATA</span>
                <button
                    onClick={() => onSourceChange('photos')}
                    style={pill(dataSource === 'photos', '#059669', '#34d399')}
                >
                    Photos
                </button>
                <button
                    onClick={() => onSourceChange('oneal')}
                    disabled={loading}
                    style={{ ...pill(dataSource === 'oneal', '#ea580c', '#f97316'), opacity: loading ? 0.5 : 1 }}
                >
                    {loading ? '...' : `O'Neal${onealCount ? ` (${onealCount})` : ''}`}
                </button>
                <button
                    onClick={() => onSourceChange('agentos')}
                    disabled={agentLoading}
                    style={{ ...pill(dataSource === 'agentos', '#7c3aed', '#a78bfa'), opacity: agentLoading ? 0.6 : 1 }}
                >
                    {`AgentOS${agentCount ? ` (${agentCount})` : ''}`}
                </button>
                {dataSource === 'agentos' && (
                    <>
                        <span style={{ color: '#475569', fontSize: '10px', marginLeft: '4px' }}>VORNE</span>
                        <button onClick={() => onAgentFaceChange?.('chat')} disabled={agentLoading} style={pill(agentFace === 'chat', '#4f46e5', '#a5b4fc')}>Chat</button>
                        <button onClick={() => onAgentFaceChange?.('card')} disabled={agentLoading} style={pill(agentFace === 'card', '#4f46e5', '#a5b4fc')}>Karte</button>
                    </>
                )}
                {agentStatus && (
                    <span style={{ color: agentStatus.startsWith('Fehler') ? '#f87171' : '#94a3b8', fontSize: '10px', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '240px' }}>{agentStatus}</span>
                )}
                <div style={{ flex: 1 }} />
                <button
                    onClick={onSort}
                    style={{ ...pill(false, '', '#38bdf8'), background: 'rgba(56,189,248,0.15)' }}
                >
                    Sort
                </button>
                <button
                    onClick={() => setExpanded(!expanded)}
                    style={{ ...pill(expanded, '#6366f1', '#818cf8'), minWidth: '28px', textAlign: 'center' }}
                >
                    {expanded ? '▴' : '▾'}
                </button>
            </div>

            {expanded && (
                <>
                    {/* Row 2: Depth fields */}
                    <div style={{ display: 'flex', gap: '4px', marginTop: '8px', alignItems: 'center', overflowX: 'auto' }}>
                        <span style={{ color: '#64748b', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: '2px', flexShrink: 0 }}>DEPTH</span>
                        {depthFields.map(f => (
                            <button
                                key={f.field}
                                onClick={() => onDepthFieldChange(f.field)}
                                style={pill(activeDepthField === f.field, '#7c3aed', '#a78bfa')}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>

                    {/* Row 3: Pivot column selector with GPANE auto-recommend */}
                    {categoricColumns.length > 0 && (
                        <div style={{ display: 'flex', gap: '4px', marginTop: '6px', alignItems: 'center', overflowX: 'auto' }}>
                            <span style={{ color: '#64748b', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: '2px', flexShrink: 0 }}>PIVOT</span>
                            {onAutoRecommend && (
                                <button
                                    onClick={onAutoRecommend}
                                    style={{ ...pill(false, '', '#fbbf24'), background: 'rgba(251,191,36,0.2)', border: '1px solid rgba(251,191,36,0.4)' }}
                                >
                                    Auto
                                </button>
                            )}
                            {categoricColumns.map(col => (
                                <button
                                    key={col}
                                    onClick={() => onColumnChange(col)}
                                    style={{
                                        ...pill(activeColumn === col, '#ea580c', '#fb923c'),
                                        ...(col === recommendedColumn && activeColumn !== col ? { border: '1px solid rgba(251,191,36,0.6)' } : {}),
                                    }}
                                >
                                    {col}{col === recommendedColumn && activeColumn !== col ? ' ★' : ''}
                                </button>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default DataIntelligenceBar;
